import { WORKSPACE_RIGHT_PANEL_NAMES } from '#packages/constants';
import { components } from '#packages/stateManagement';
import { biLogger, hoc } from '#packages/util';
import {
  aiAssistantMessageSrc38Evid2103,
  aiAssistantPanelOpenSrc38Evid2101,
} from '@wix/bi-logger-editor-data/v2';
import { useChatServiceProvider } from '@wix/chat-service-sdk';
import type { OnWidgetAction } from '@wix/chat-widget-common-widgets';
import type { ChatMessageData } from '@wix/chat-widget-ui-lib';
import { ChatWidget, SidebarEnvironment } from '@wix/chat-widget-ui-lib';
import { HttpClient } from '@wix/http-client';
import React, {
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type FC,
} from 'react';
import {
  IS_FIRST_ASSISTANT_MESSAGE,
  mapDispatchToProps,
  mapStateToProps,
} from './aiAssistantMapper';
import type { AiAssistantProps } from './aiAssistantTypes';
import { biWidgetActionMapper, getWidgetMessage } from './helpers';
import { getEditorWidgets } from './widgets';
import type { ACTIONS } from './widgets/constants';

const httpClient = new HttpClient();

const AiAssistantSidebar: FC<AiAssistantProps> = ({
  config,
  closeAiAssistant,
  setToUserPreferences,
  isFirstMessage,
}) => {
  const [chatMessages, setChatMessages] = useState([]);
  const [isCustomMessage, setIsCustomMessage] = useState(false);

  const {
    createConversation,
    sendUserMessage,
    messages,
    statusMessage,
    conversationId,
  } = useChatServiceProvider({
    httpClient,
    domainIds: config.domainIds,
    onFunctionCall: (name) => {
      return { success: true, response: { name } };
    },
  });

  const onWidgetAction: OnWidgetAction = (widgetName, data) => {
    config.onWidgetAction?.(widgetName, data);

    biWidgetActionMapper({
      assistantName: config.assistantName,
      origin: config.origin,
      widgetName,
      data,
    })[data.action as keyof typeof ACTIONS]();
  };

  const widgets = getEditorWidgets({
    send: async (content: string): Promise<void> => {
      config.onSendMessage?.(content);
      sendUserMessage(content);
    },
    onWidgetAction,
    theme: config.theme,
  });

  const startConversation = useCallback(async () => {
    await createConversation({
      origin: config.origin,
      ...(config.firstUserMessage && {
        firstUserMessage: config.firstUserMessage,
      }),
      ...(config.firstAssistantMessageContent && {
        firstAssistantMessageContent: config.firstAssistantMessageContent,
      }),

      tools: config.tools,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createConversation]);

  const onClearChat = () => {
    startConversation();
  };

  const onSendMessage = (message: ChatMessageData) => {
    sendUserMessage(message.content);
    config.onSendMessage?.(message.content);
    setIsCustomMessage(true);
  };

  const isFirstMessageSent = useRef(false);

  useEffect(() => {
    if (!isFirstMessageSent.current) {
      isFirstMessageSent.current = true;
      startConversation();

      biLogger.report(
        aiAssistantPanelOpenSrc38Evid2101({
          isFirstMessage,
          target: config.assistantName,
          panel_name: WORKSPACE_RIGHT_PANEL_NAMES.AI_ASSISTANT,
          origin: config.origin,
        }),
      );

      setToUserPreferences(IS_FIRST_ASSISTANT_MESSAGE, false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startConversation]);

  const onClose = () => {
    config.onCloseChat?.();
    closeAiAssistant();
  };

  useEffect(() => {
    if (conversationId)
      setChatMessages([
        ...(config.firstWidget ? [getWidgetMessage(config.firstWidget)] : []),
        ...messages,
      ]);
  }, [messages, config.firstWidget, conversationId]);

  useEffect(() => {
    if (chatMessages.length === 0) return;

    const lastMessage: ChatMessageData = chatMessages[chatMessages.length - 1];
    console.log('lastMessage', lastMessage);

    if (!lastMessage.content) return;

    biLogger.report(
      aiAssistantMessageSrc38Evid2103({
        target: config.assistantName,
        role: lastMessage.role,
        isCustom: isCustomMessage,
        text: lastMessage.content,
      }),
    );

    setIsCustomMessage(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMessages]);

  return (
    <SidebarEnvironment
      title={config.assistantName}
      onClean={onClearChat}
      onClose={onClose}
      isFooterVisible={false}
    >
      <ChatWidget
        chatMessages={chatMessages}
        widgets={widgets}
        onSendMessage={onSendMessage}
        statusMessage={statusMessage}
        assistantName={config.assistantName}
        onUserStartTyping={config.onUserStartTyping}
        onWidgetAction={config.onWidgetAction}
        messageToSendPlaceholder={config.messagePlaceholder}
      />
    </SidebarEnvironment>
  );
};

const {
  connect,
  STORES: { EDITOR_API },
} = hoc;

const ConnectedAiAssistantSidebar = components.hoc.compPanel(
  connect(
    EDITOR_API,
    mapStateToProps,
    mapDispatchToProps,
  )(memo(AiAssistantSidebar)),
);

export default ConnectedAiAssistantSidebar;
